import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Lock, Check, Sparkles, Zap, BookOpen, Trophy, Star, Shield, Crown, Gem } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLifeCountdown } from '../components/app/PromptOriginLifeSection';

export const PaywallPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const lifeCountdown = useLifeCountdown(user?.life_countdown_start || user?.created_at);

  const completedDays = user?.completed_days || [];

  const handleCheckout = async (plan) => {
    setLoading(true);
    try {
      const res = await api.post('/subscription/checkout', {
        plan,
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error('Wystąpił błąd. Spróbuj ponownie.');
      setLoading(false);
    }
  };

  if (user?.subscription_status === 'active') {
    return <Navigate to="/" replace />;
  }

  const benefits = [
    { icon: BookOpen, text: 'Stały dostęp do aktualizowanych materiałów', highlight: true },
    { icon: Sparkles, text: '100+ gotowych promptów w bibliotece' },
    { icon: Trophy, text: 'Certyfikat ukończenia' },
    { icon: Zap, text: 'Codzienne nowe treści i scenariusze' },
    { icon: Star, text: 'Priorytetowe wsparcie' },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)', background: 'transparent' }}>
      <div className="px-6 pt-8 pb-6">
        {/* Back button */}
        <button onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-6 h-6 text-slate-400 hover:text-white transition-colors" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-sky-500 via-emerald-500 to-sky-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-sky-500/30">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Odblokuj pełny dostęp</h1>
          <p className="text-slate-400">Zainwestuj w swoje umiejętności AI</p>
        </div>

        {/* Benefits Section */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Co otrzymujesz</h2>
          <Card className="border-0" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)' }}>
            <CardContent className="py-5 space-y-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    benefit.highlight 
                      ? 'bg-gradient-to-br from-sky-500 to-emerald-500' 
                      : 'bg-emerald-500/20'
                  }`}>
                    <benefit.icon className={`w-4 h-4 ${benefit.highlight ? 'text-white' : 'text-emerald-400'}`} />
                  </div>
                  <span className={`${benefit.highlight ? 'text-white font-medium' : 'text-slate-300'}`}>
                    {benefit.text}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Pricing Section */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Wybierz plan</h2>
          
          <div className="space-y-3">
            {/* Yearly Plan - Best Value */}
            <Card 
              className={`cursor-pointer transition-all duration-200 border-2 ${
                selectedPlan === 'yearly' 
                  ? 'border-sky-500 ring-2 ring-sky-500/30' 
                  : 'border-transparent hover:border-slate-600'
              }`}
              style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}
              onClick={() => setSelectedPlan('yearly')}
            >
              <CardContent className="py-5">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-gradient-to-r from-sky-500 to-emerald-500 text-white px-2.5 py-1 rounded-full font-semibold">
                        OSZCZĘDŹ 40%
                      </span>
                      <span className="text-xs text-amber-400 font-medium">Najpopularniejszy</span>
                    </div>
                    <h3 className="font-bold text-white text-lg">Plan roczny</h3>
                    <p className="text-sm text-slate-400">Pełny rok dostępu</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">250 <span className="text-lg text-slate-400">zł</span></p>
                    <p className="text-sm text-emerald-400 font-medium">tylko 20,83 zł/mies.</p>
                  </div>
                </div>
                {selectedPlan === 'yearly' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Plan */}
            <Card 
              className={`cursor-pointer transition-all duration-200 border-2 ${
                selectedPlan === 'monthly' 
                  ? 'border-sky-500 ring-2 ring-sky-500/30' 
                  : 'border-transparent hover:border-slate-600'
              }`}
              style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)' }}
              onClick={() => setSelectedPlan('monthly')}
            >
              <CardContent className="py-5">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-white">Plan miesięczny</h3>
                    <p className="text-sm text-slate-400">Elastyczne rozwiązanie</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-300">35 <span className="text-base text-slate-500">zł</span></p>
                    <p className="text-sm text-slate-500">/ miesiąc</p>
                  </div>
                </div>
                {selectedPlan === 'monthly' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Button for standard plans */}
        <Button 
          onClick={() => handleCheckout(selectedPlan)}
          disabled={loading || selectedPlan === 'life'}
          className="w-full h-14 rounded-2xl text-lg font-semibold transition-all duration-200 bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
        >
          {loading ? (
            <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            `Kup teraz – ${selectedPlan === 'yearly' ? '250 zł' : '35 zł'}`
          )}
        </Button>

        {/* Free trial info */}
        <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-center text-sm text-emerald-400 font-medium">
            Pierwsze 3 dni masz za darmo — możesz przetestować przed zakupem
          </p>
        </div>
{/* PromptOrigin Life — exclusive 3rd tier */}
<div className="mt-8 mb-2">
  <div className="flex items-center gap-2 mb-3">
    <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(138,77,255,0.3), transparent)' }} />
    <span className="text-xs uppercase tracking-widest text-violet-400/60 font-medium">Najwyższy poziom</span>
    <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(138,77,255,0.3), transparent)' }} />
  </div>
</div>

        {/* PromptOrigin Life — exclusive 3rd tier */}
        <div className="mt-8 mb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(138,77,255,0.3), transparent)' }} />
            <span className="text-xs uppercase tracking-widest text-violet-400/60 font-medium">Najwyższy poziom</span>
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(138,77,255,0.3), transparent)' }} />
          </div>
        </div>

        <div
          className="rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer"
          style={{
            background: 'linear-gradient(160deg, rgba(95,46,255,0.12) 0%, rgba(138,77,255,0.06) 50%, rgba(139,233,255,0.03) 100%)',
            border: '1px solid rgba(138,77,255,0.20)',
            boxShadow: '0 4px 30px rgba(95,46,255,0.10)',
          }}
          onClick={() => setSelectedPlan('life')}
          data-testid="plan-life"
        >
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #5F2EFF, #8A4DFF)',
                    boxShadow: '0 4px 16px rgba(95,46,255,0.35)',
                  }}
                >
                  <Gem className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-base">PromptOrigin Life</h3>
                  <p className="text-xs text-slate-500">Ekskluzywny pomocnik AI</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold" style={{ color: '#8A4DFF' }}>300 <span className="text-sm text-slate-500">zł</span></p>
                <p className="text-xs text-slate-500">/ miesiąc</p>
              </div>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed mb-4">
              Osobisty, inteligentny towarzysz AI — przygotowywany indywidualnie dla każdego użytkownika. Najwyższy poziom doświadczenia w PromptOrigin.
            </p>

            {/* Compact countdown */}
            {lifeCountdown ? (
              <div
                className="rounded-xl px-3 py-2.5 flex items-center justify-between"
                style={{ background: 'rgba(95,46,255,0.08)', border: '1px solid rgba(138,77,255,0.12)' }}
              >
                <span className="text-[11px] text-violet-300/70 uppercase tracking-wider font-medium">Odblokowanie za</span>
                <div className="flex items-center gap-1.5">
                  {[
                    { v: lifeCountdown.days, l: 'd' },
                    { v: lifeCountdown.hours, l: 'h' },
                    { v: lifeCountdown.minutes, l: 'm' },
                  ].map((u, i) => (
                    <span key={i} className="text-sm font-bold text-white">
                      {String(u.v).padStart(2, '0')}<span className="text-violet-400/50 text-xs font-normal">{u.l}</span>
                      {i < 2 && <span className="text-violet-400/30 mx-0.5">:</span>}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div
                className="rounded-xl px-3 py-2.5 text-center"
                style={{ background: 'rgba(95,46,255,0.08)', border: '1px solid rgba(138,77,255,0.12)' }}
              >
                <span className="text-xs font-semibold text-violet-300">Gotowe do aktywacji</span>
              </div>
            )}
          </div>

          {selectedPlan === 'life' && (
            <div
              className="px-5 pb-4"
            >
              <Button
                onClick={(e) => { e.stopPropagation(); handleCheckout('life'); }}
                disabled={loading || !!lifeCountdown}
                className="w-full h-12 rounded-xl text-sm font-semibold text-white transition-all"
                style={{
                  background: lifeCountdown
                    ? 'rgba(95,46,255,0.15)'
                    : 'linear-gradient(135deg, #5F2EFF, #8A4DFF)',
                  boxShadow: lifeCountdown ? 'none' : '0 4px 20px rgba(95,46,255,0.3)',
                  opacity: lifeCountdown ? 0.6 : 1,
                }}
                data-testid="buy-life-btn"
              >
                {lifeCountdown ? 'Dostępne po zakończeniu odliczania' : 'Aktywuj PromptOrigin Life — 300 zł/mies.'}
              </Button>
            </div>
          )}
        </div>

        {/* Trust badges */}
        <div className="mt-8 py-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm mb-2">
            <Shield className="w-4 h-4" />
            <span>Bezpieczna płatność</span>
          </div>
          <p className="text-xs text-slate-500">
            Płatność obsługiwana przez Stripe. Możesz anulować w dowolnym momencie.
          </p>
        </div>
      </div>
    </div>
  );
};
